import { Route, Switch, Redirect, useHistory } from "react-router-dom";
import { useMemo } from "react";
import IntegrationOverview from "../components/integrations/IntegrationOverview";
import IntegrationsDashboard from "../components/integrations/dashboard/IntegrationsDashboard";
import ScraperEditorRoot from "../components/scraper/ScraperEditorRoot";
import { fetchCurrentUser, hasAuthToken } from "../api-client/api_client";
import {
  ADMIN_ROOT_PATH,
  BLUEPRINT_EDITOR_PATH,
  INTEGRATION_DASHBOARD_PATH,
  INTEGRATION_OVERVIEW_PATH,
  LOGIN_PATH,
  SCRAPER_EDITOR_PATH,
  MAPPING_TEST_EDITOR_PATH,
  SCRAPER_HISTORY_PATH,
  INTEGRATIONS_EXPORT_PATH,
  SCRAPER_VERSION_EDITOR_PATH,
  REPORT_TEMPLATE_EDIT_PATH,
  REPORT_TEMPLATE_CREATE_PATH,
  INTEGRATION_BUILDER_PATH,
  INTEGRATION_BUILDER_PATH_INITIALIZATION,
  ALERTS_DASHBOARD_PATH,
  CANARY_TEST_DASHBOARD_PATH,
  CONDUCTOR_PATH,
  SYNC_PLAN_DASHBOARD_PATH,
  CODE_BLOCK_PROGRESS_PATH,
  CODE_BLOCK_PROGRESS_INTEGRATION_PATH,
} from "./RouterUtils";
import MappingTestEditorRoot from "../components/mapping-tests/root/MappingTestEditorRoot";
import ScraperHistoryRoot from "../components/integrations/scrapers/ScraperHistoryRoot";
import IntegrationsExportPage from "../components/integrations/IntegrationsExportPage";
import BlueprintEditor from "../components/blueprint-editor/BlueprintEditor";
import ReportTemplateEditForm from "../components/integrations/report-templates/ReportTemplateEditForm";
import IntegrationBuilder from "../components/integration-builder/IntegrationBuilder";
import { useEffect, useState } from "react";
import { User } from "src/models/Entities";
import AlertDashboard from "../components/alerts/dashboard/AlertDashboard";
import { MergeFlagFeature, useMergeFlag } from "../../src/components/shared/hooks/useMergeFlag";
import TopNavigationBar from "../components/portal/TopNavigationBar";
import CanaryTestingDashboard from "../components/canary-test-dashboard/CanaryTestingDashboard";
import SyncPlanVisualizationTab from "../components/integrations/sync-plan-visualization/SyncPlanVisualizationTab";
import ConductorAdminPage from "../components/conductor/admin/ConductorAdminPage";
import CodeBlockProgressPage from "../components/integrations/CodeBlockProgressPage";
import CodeBlockProgressIntegrationPage from "../components/integrations/CodeBlockProgressIntegrationPage";

const AdminPortalRouter = () => {
  const [user, setUser] = useState<User>();
  const history = useHistory();
  const { enabled: isAlertsTabEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_ALERT_DASHBOARD,
    isEnabledForUser: true,
  });

  const ID = "[a-f0-9-]+";

  const noNavbarPathPatterns = [
    // Blueprint editor
    new RegExp(`^/integrations/${ID}/blueprints/versions/${ID}/editor/main$`),
    // Mapping test
    new RegExp(`^/integrations/${ID}/tests/${ID}/editor/main$`),
    // Scraper editor
    new RegExp(`^/integrations/${ID}/scrapers/${ID}/versions/${ID}/editor$`),
    // Report templates edit
    new RegExp(`^/integrations/${ID}/report-templates/${ID}/edit$`),
    // Report templates create
    new RegExp(`^/integrations/${ID}/report-templates/create$`),
    // Blueprint readme tab
    new RegExp(`^/integrations/${ID}/blueprints/versions/${ID}/editor/readme$`),
    // Blueprint validation tab
    new RegExp(`^/integrations/${ID}/blueprints/versions/${ID}/editor/validation$`),
    // Blueprint return schema tab
    new RegExp(`^/integrations/${ID}/blueprints/versions/${ID}/editor/return-schema$`),
  ];

  const shouldDisplayTopNavBar = useMemo(() => {
    return !noNavbarPathPatterns.some((pattern) => pattern.test(history.location.pathname));
  }, [history.location.pathname]);

  useEffect(() => {
    if (!user && hasAuthToken()) {
      fetchCurrentUser(setUser);
    }
  }, [hasAuthToken, user, setUser]);

  const fsLoaded = "FS" in window;

  useEffect(() => {
    if (user && fsLoaded && process.env.REACT_APP_MERGE_ENV === "PRODUCTION") {
      const userVars: any = {
        displayName: user.name,
        email: user.email,
      };

      // @ts-ignore
      window.FS.identify(user.uuid.toString(), userVars);
    }
  }, [user, fsLoaded]);

  if (!hasAuthToken()) {
    return (
      <Redirect to={LOGIN_PATH + "?redirect=" + encodeURIComponent(window.location.pathname)} />
    );
  }

  return (
    <>
      {shouldDisplayTopNavBar ? <TopNavigationBar isAlertTabEnabled={isAlertsTabEnabled} /> : null}

      <Switch>
        <Route exact path={INTEGRATION_DASHBOARD_PATH} component={IntegrationsDashboard} />
        <Route exact path={ALERTS_DASHBOARD_PATH} component={AlertDashboard} />
        <Route exact path={CANARY_TEST_DASHBOARD_PATH} component={CanaryTestingDashboard} />
        <Route exact path={SYNC_PLAN_DASHBOARD_PATH} component={SyncPlanVisualizationTab} />
        <Route path={CONDUCTOR_PATH} component={ConductorAdminPage} />
        <Route
          exact
          path={CODE_BLOCK_PROGRESS_INTEGRATION_PATH}
          component={CodeBlockProgressIntegrationPage}
        />
        <Route exact path={CODE_BLOCK_PROGRESS_PATH} component={CodeBlockProgressPage} />
        <Route
          exact
          path={`${ALERTS_DASHBOARD_PATH}/:category?/:alertID?`}
          component={AlertDashboard}
        />
        <Route
          path={[REPORT_TEMPLATE_EDIT_PATH, REPORT_TEMPLATE_CREATE_PATH]}
          component={ReportTemplateEditForm}
        />
        <Route path={BLUEPRINT_EDITOR_PATH} component={BlueprintEditor} />
        <Route
          path={`${INTEGRATION_BUILDER_PATH}/:integrationID?`}
          component={IntegrationBuilder}
        />
        <Route path={INTEGRATION_BUILDER_PATH_INITIALIZATION} component={IntegrationBuilder} />
        <Route
          path={SCRAPER_EDITOR_PATH}
          children={({ match }: { match: any }) => <ScraperEditorRoot match={match} />}
        />
        <Route
          path={SCRAPER_VERSION_EDITOR_PATH}
          children={({ match }: { match: any }) => <ScraperEditorRoot match={match} />}
        />
        <Route
          path={MAPPING_TEST_EDITOR_PATH}
          children={({ match }: { match: any }) => <MappingTestEditorRoot match={match} />}
        />
        <Route path={SCRAPER_HISTORY_PATH} component={ScraperHistoryRoot} />
        <Route path={INTEGRATIONS_EXPORT_PATH} component={IntegrationsExportPage} />
        <Route
          path={INTEGRATION_OVERVIEW_PATH}
          children={({ match }: { match: any }) => <IntegrationOverview match={match} />}
        />
        <Redirect from={ADMIN_ROOT_PATH} to={INTEGRATION_DASHBOARD_PATH} />
      </Switch>
      {/* End Integrations/Common Models Pages */}
    </>
  );
};

export default AdminPortalRouter;
